import React, { useState, useRef, useEffect } from 'react';
// import CompanyMark, { Sprout } from './components/CompanyMark'; // FOOTER_DISABLED — restore with footer
import { Sprout } from './components/CompanyMark';
import { PAST_GATHERINGS } from './lib/landingData';

// TODO: Replace with your Google Apps Script deployment URL
const SCRIPT_URL = 'https://script.google.com/macros/s/AKfycbzFxpoYSShWP7w01pI-CSfCdWQPdXe3MIYpBp96HDPQzXuNL4VG0lcmnq_82arTpt2IxA/exec';

// Cover image of each gathering only, most-recent first
const ALL_PHOTOS = [...PAST_GATHERINGS].reverse().map(g => g.images[0]);

const STACK_CONFIGS = {
  sparse: {
    count: 3,
    rotations: [-8, -18, 10],
    offsets:   [[40, 20], [5, -10], [115, -15]],
    //          i=0 front (zIdx=3)   i=1 back-left   i=2 back-right (zIdx=1)
  },
  deep: {
    count: 6,
    rotations: [-8, -18, 10,  15, -22, -2],
    offsets: [
      [40,  20],  // i=0 front      — same as sparse
      [5,  -10],  // i=1 back-left  — same as sparse
      [115, -15], // i=2 back-right — same as sparse
      [55,  18],  // i=3 — tilts right, center — within fan width
      [18,   2],  // i=4 — tilts hard left, left-center — within fan width
      [80,  22],  // i=5 — nearly upright, right-center — within fan width
    ],
  },
  peek: {
    count: 5,
    rotations: [-8, -18, 10, -30, 22],
    offsets: [
      [40,  20],  // i=0 front      — same as sparse
      [5,  -10],  // i=1 back-left  — same as sparse
      [115, -15], // i=2 back-right — same as sparse
      [-20,  -5], // i=3 deep-left  — peeks 25px left of i=1
      [140, -20], // i=4 deep-right — peeks 25px right of i=2
    ],
  },
  dense: {
    count: 5,
    rotations: [-10, -20, -5, 12, 18],
    offsets:   [[20, 20], [-15, -5], [5, -10], [65, -5], [100, -20]],
  },
};

export default function WaterForPlantsLanding() {
  const [email, setEmail] = useState('');
  const [honeypot, setHoneypot] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [emailError, setEmailError] = useState('');
  const [suggestedEmail, setSuggestedEmail] = useState('');
  const [correctionApplied, setCorrectionApplied] = useState(false);
  const [mounted, setMounted] = useState(false);

  // Carousel state
  const [carouselOpen, setCarouselOpen] = useState(false);
  const [carouselIndex, setCarouselIndex] = useState(0);
  const [animPhase, setAnimPhase] = useState('idle'); // 'idle'|'entering'|'open'|'closing'
  const [hoveredCard, setHoveredCard] = useState(null);

  // Rate limiting
  const lastSubmitRef = useRef(0);
  const submitCountRef = useRef(0);

  // Carousel refs
  const isMobileRef = useRef(typeof window !== 'undefined' && window.innerWidth < 768);
  const animTimersRef = useRef([]);
  const scrollRef = useRef(null);

  // Stack variant: ?stack=deep|dense|peek
  const stackParam = typeof window !== 'undefined' ? new URLSearchParams(window.location.search).get('stack') : null;
  const stackMode = stackParam === 'dense' ? 'dense' : stackParam === 'peek' ? 'peek' : stackParam === 'sparse' ? 'sparse' : 'deep';
  const config = STACK_CONFIGS[stackMode];

  // Extended disposable email domains
  const disposableDomains = new Set([
    'tempmail.com', 'throwaway.com', 'mailinator.com', 'guerrillamail.com', 'temp-mail.org',
    '10minutemail.com', 'fakeinbox.com', 'trashmail.com', 'getnada.com', 'maildrop.cc',
    'yopmail.com', 'sharklasers.com', 'dispostable.com', 'mintemail.com', 'tempail.com',
    'mohmal.com', 'fakemailgenerator.com', 'emailondeck.com', 'tempr.email', 'discard.email',
    'tmpmail.org', 'tmpmail.net', 'throwawaymail.com', 'getairmail.com', 'moakt.com',
    'tempinbox.com', 'burnermail.io', 'mailcatch.com', 'mailnesia.com', 'spamgourmet.com'
  ]);

  // Common typo corrections
  const typoCorrections = {
    'gmial.com': 'gmail.com', 'gmal.com': 'gmail.com', 'gmail.con': 'gmail.com',
    'gmail.co': 'gmail.com', 'gamil.com': 'gmail.com', 'gnail.com': 'gmail.com',
    'hotmal.com': 'hotmail.com', 'hotmail.con': 'hotmail.com', 'hotmial.com': 'hotmail.com',
    'yahooo.com': 'yahoo.com', 'yaho.com': 'yahoo.com', 'yahoo.con': 'yahoo.com',
    'outloo.com': 'outlook.com', 'outlok.com': 'outlook.com', 'outlook.con': 'outlook.com',
    'icloud.con': 'icloud.com', 'icoud.com': 'icloud.com'
  };

  const validateEmail = (email) => {
    const trimmed = email.trim().toLowerCase();
    if (!trimmed) return 'Email is required';

    if (trimmed.length > 254) return 'Email is too long';

    const emailRegex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*\.[a-zA-Z]{2,}$/;
    if (!emailRegex.test(trimmed)) return 'Please enter a valid email';

    const [localPart, domain] = trimmed.split('@');

    if (localPart.length > 64) return 'Email username is too long';
    if (localPart.startsWith('.') || localPart.endsWith('.')) return 'Please enter a valid email';
    if (localPart.includes('..')) return 'Please enter a valid email';

    if (disposableDomains.has(domain)) return 'Please use a permanent email';

    if (typoCorrections[domain]) {
      const corrected = `${localPart}@${typoCorrections[domain]}`;
      return { error: 'typo', corrected };
    }

    if (/^(test|fake|spam|noreply|no-reply|asdf|qwerty)/i.test(localPart)) {
      return 'Please use your real email';
    }

    return '';
  };

  const checkRateLimit = () => {
    const now = Date.now();
    const timeSinceLastSubmit = now - lastSubmitRef.current;
    if (timeSinceLastSubmit > 60000) submitCountRef.current = 0;
    if (submitCountRef.current >= 3 && timeSinceLastSubmit < 60000) {
      return 'Too many attempts. Please wait a moment.';
    }
    return '';
  };

  const handleEmailChange = (e) => {
    setEmail(e.target.value);
    if (emailError) setEmailError('');
    if (suggestedEmail) setSuggestedEmail('');
    if (correctionApplied) setCorrectionApplied(false);
  };

  const applyCorrection = () => {
    setEmail(suggestedEmail);
    setEmailError('');
    setSuggestedEmail('');
    setCorrectionApplied(true);
    setTimeout(() => setCorrectionApplied(false), 2000);
  };

  const handleSubmit = async () => {
    if (honeypot) {
      setSubmitted(true);
      setTimeout(() => setSubmitted(false), 5000);
      return;
    }

    const rateLimitError = checkRateLimit();
    if (rateLimitError) { setEmailError(rateLimitError); return; }

    const result = validateEmail(email);
    if (result) {
      if (typeof result === 'object' && result.error === 'typo') {
        setEmailError(`Did you mean ${result.corrected}?`);
        setSuggestedEmail(result.corrected);
      } else {
        setEmailError(result);
        setSuggestedEmail('');
      }
      return;
    }

    const trimmedEmail = email.trim().toLowerCase();
    lastSubmitRef.current = Date.now();
    submitCountRef.current += 1;
    setSubmitting(true);
    setEmailError('');

    try {
      const formData = new FormData();
      formData.append('email', trimmedEmail);
      const response = await fetch(SCRIPT_URL, { method: 'POST', body: formData });
      const data = await response.json();

      if (data.status === 'success') {
        setSuccessMessage("Thanks! You'll hear from us soon.");
        setSubmitted(true);
        setEmail('');
      } else if (data.status === 'already_subscribed') {
        setSuccessMessage("You're already on the list!");
        setSubmitted(true);
        setEmail('');
      } else {
        setEmailError(data.message || 'Something went wrong. Please try again.');
      }
    } catch (error) {
      console.error('Submission error:', error);
      setEmailError('Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // ─── Carousel ────────────────────────────────────────────────────────────────

  const openCarousel = (clickedIndex) => {
    animTimersRef.current.forEach(clearTimeout);
    animTimersRef.current = [];
    setCarouselIndex(clickedIndex);
    setCarouselOpen(true);
    requestAnimationFrame(() => requestAnimationFrame(() => {
      setAnimPhase('entering');
      animTimersRef.current.push(setTimeout(() => setAnimPhase('open'), 300));
    }));
  };

  const closeCarousel = () => {
    animTimersRef.current.forEach(clearTimeout);
    animTimersRef.current = [];
    setAnimPhase('closing');
    animTimersRef.current.push(setTimeout(() => {
      setCarouselOpen(false);
      setAnimPhase('idle');
    }, 300));
  };

  // Scroll to clicked card when carousel opens
  useEffect(() => {
    if (!carouselOpen || !scrollRef.current) return;
    const CARD_W = isMobileRef.current ? 220 : 260;
    scrollRef.current.scrollLeft = carouselIndex * (CARD_W + 16);
  }, [carouselOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  // Sync carouselIndex from scroll position
  useEffect(() => {
    const el = scrollRef.current;
    if (!el || !carouselOpen) return;
    const CARD_W = isMobileRef.current ? 220 : 260;
    const onScroll = () => setCarouselIndex(Math.round(el.scrollLeft / (CARD_W + 16)));
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => el.removeEventListener('scroll', onScroll);
  }, [carouselOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  // Keyboard + Escape
  useEffect(() => {
    if (!carouselOpen) return;

    const close = () => {
      animTimersRef.current.forEach(clearTimeout);
      animTimersRef.current = [];
      setAnimPhase('closing');
      animTimersRef.current.push(setTimeout(() => {
        setCarouselOpen(false);
        setAnimPhase('idle');
      }, 300));
    };

    const handleKeyDown = (e) => {
      const CARD_W = isMobileRef.current ? 220 : 260;
      if (e.key === 'ArrowLeft')  scrollRef.current?.scrollBy({ left: -(CARD_W + 16), behavior: 'smooth' });
      if (e.key === 'ArrowRight') scrollRef.current?.scrollBy({ left:  (CARD_W + 16), behavior: 'smooth' });
      if (e.key === 'Escape')     close();
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [carouselOpen]);

  // Page mount animation
  useEffect(() => {
    const id = requestAnimationFrame(() => requestAnimationFrame(() => setMounted(true)));
    return () => cancelAnimationFrame(id);
  }, []);

  // ─── Render ──────────────────────────────────────────────────────────────────

  return (
    <div className="page-root" style={{ background: '#fff', display: 'flex', flexDirection: 'column', overflowX: 'hidden' }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Averia+Serif+Libre:wght@400;700&family=IBM+Plex+Mono:wght@400;500&display=swap');
        body {
          margin: 0;
        }
        .page-root {
          min-height: 100vh;
          min-height: 100svh;
        }
        *, *::before, *::after {
          box-sizing: border-box;
          -webkit-font-smoothing: antialiased;
          -moz-osx-font-smoothing: grayscale;
          text-rendering: optimizeLegibility;
        }
        /* Polaroid cards keep content-box so width = photo area, not total card */
        .polaroid-card, .polaroid-stack-card {
          box-sizing: content-box;
        }
        body, input, button {
          font-family: 'IBM Plex Mono', monospace;
          letter-spacing: -0.01em;
          -webkit-font-smoothing: antialiased;
          -moz-osx-font-smoothing: grayscale;
        }
        .alegreya { font-family: 'Averia Serif Libre', serif; font-weight: 700; letter-spacing: -0.035em; }
        .email-input {
          border: 1px solid #D1D5DB;
          border-radius: 6px;
          transition: border-color 0.3s ease-out, box-shadow 0.3s ease-out, transform 0.3s ease-out;
        }
        .email-input:focus {
          outline: none;
          border-color: #9CA3AF;
          box-shadow: 0 4px 14px rgba(0,0,0,0.08), 0 2px 6px rgba(0,0,0,0.06);
          transform: translateY(-2px);
        }
        .email-input.error {
          border-color: #F87171;
        }
        .submit-btn {
          background-color: #EF4444;
          border-radius: 6px;
          transition: background-color 0.2s ease, transform 0.1s ease;
        }
        .submit-btn:hover {
          background-color: #F87171;
        }
        .submit-btn:active {
          transform: scale(0.98);
        }
        .yes-link {
          display: inline-block;
          cursor: pointer;
          color: #EF4444;
          margin-left: 6px;
          padding: 2px 8px;
          border: 1px solid #EF4444;
          border-radius: 4px;
          background: transparent;
          transition: background-color 0.15s ease-out, color 0.15s ease-out;
        }
        .yes-link:hover {
          background-color: #EF4444;
          color: white;
        }

        /* ── Polaroid frame ── */
        .polaroid-card {
          background: #fff;
          border-radius: 4px;
          padding: 10px;
          box-shadow: 0 4px 15px rgba(0,0,0,0.15), 0 1px 4px rgba(0,0,0,0.1);
        }
        .polaroid-card img {
          width: 100%;
          aspect-ratio: 3/4;
          object-fit: cover;
          display: block;
          border-radius: 2px;
        }

        /* ── Polaroid stack ── */
        .polaroid-stack-card {
          position: absolute;
          top: 0;
          left: 0;
          width: 140px;
          cursor: pointer;
          user-select: none;
          -webkit-user-select: none;
          transition: transform 0.2s ease-out;
        }

        /* ── Carousel scroll container ── */
        .carousel-scroll {
          display: flex;
          align-items: center;
          overflow-x: auto;
          overflow-y: hidden;
          scroll-snap-type: x mandatory;
          scroll-behavior: smooth;
          -webkit-overflow-scrolling: touch;
          gap: 16px;
          padding-top: 24px;
          padding-bottom: 24px;
          padding-inline: calc(50vw - 110px);
          scrollbar-width: none;
          -ms-overflow-style: none;
        }
        .carousel-scroll::-webkit-scrollbar { display: none; }
        .carousel-scroll-item {
          flex-shrink: 0;
          scroll-snap-align: center;
        }
        .polaroid-carousel-card {
          width: 220px;
        }
        @media (min-width: 768px) {
          .carousel-scroll {
            padding-inline: calc(50vw - 130px);
          }
          .polaroid-carousel-card {
            width: 260px;
          }
        }

        /* ── Close button ── */
        .carousel-close-btn {
          position: fixed;
          bottom: max(16px, calc(25vh - 104px));
          left: 50%;
          transform: translateX(-50%);
          z-index: 210;
          background: rgba(255,255,255,0.92);
          border: 1px solid #E5E7EB;
          border-radius: 50%;
          width: 40px;
          height: 40px;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          font-size: 20px;
          color: #374151;
          line-height: 1;
          padding: 0;
          transition: background 0.15s ease, border-color 0.15s ease;
        }
        .carousel-close-btn:hover {
          background: #F3F4F6;
          border-color: #9CA3AF;
        }
        @media (min-width: 768px) {
          .carousel-close-btn {
            bottom: auto;
            left: auto;
            transform: none;
            top: max(16px, calc(50vh - 333px));
            right: max(16px, calc(50vw - 230px));
          }
        }

        /* ── Page load-in ── */
        .page-enter {
          opacity: 0;
          transform: translateY(10px);
        }
        .page-enter-stack {
          transition: opacity 0.4s ease, transform 0.4s ease;
        }
        .page-enter-text {
          transition: opacity 0.4s ease, transform 0.4s ease;
          transition-delay: 140ms;
        }
        .page-enter-form {
          transition: opacity 0.4s ease, transform 0.4s ease;
          transition-delay: 220ms;
        }
      `}</style>

      {/* Main Content */}
      <main style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', padding: '64px 32px', maxWidth: '315px', margin: '0 auto', width: '100%' }}>

        {/* Polaroid Stack */}
        <div
          className={!mounted ? 'page-enter page-enter-stack' : 'page-enter-stack'}
          style={{
          position: 'relative',
          width: '280px',
          height: '240px',
          margin: '0 auto 16px',
          alignSelf: 'center',
          opacity: !mounted ? 0 : (animPhase === 'entering' || animPhase === 'open' ? 0 : 1),
          transition: 'opacity 0.3s ease 60ms, transform 0.4s ease 60ms',
          pointerEvents: animPhase === 'entering' || animPhase === 'open' ? 'none' : 'auto',
        }}>
          {ALL_PHOTOS.slice(0, config.count).map((src, i) => {
            const [dx, dy] = config.offsets[i];
            const rotation = config.rotations[i];
            const hoverLift = hoveredCard === i ? -10 : 0;
            return (
              <div
                key={i}
                className="polaroid-card polaroid-stack-card"
                style={{
                  transform: `translate(${dx}px, ${dy + hoverLift}px) rotate(${rotation}deg)`,
                  zIndex: config.count - i,
                }}
                onClick={() => openCarousel(i)}
                onMouseEnter={() => setHoveredCard(i)}
                onMouseLeave={() => setHoveredCard(null)}
              >
                <img src={src} alt={`Water for Plants gathering ${i + 1}`} />
              </div>
            );
          })}
        </div>

        {/* Title + Subtitle */}
        <div className={!mounted ? 'page-enter page-enter-text' : 'page-enter-text'}>
          <h1 className="alegreya" style={{ fontSize: '48px', lineHeight: '1', color: '#374151', marginBottom: '16px', fontWeight: 700 }}>
            Water<br />
            for Plants <Sprout size="display-md" />
          </h1>

          <p style={{ fontSize: '16px', lineHeight: '1.55', color: '#6B7280', marginBottom: '32px', fontFamily: "'IBM Plex Mono', monospace" }}>
            Get notified when the next<br />
            circle opens.
          </p>
        </div>

        {/* Email Form */}
        <div className={!mounted ? 'page-enter page-enter-form' : 'page-enter-form'}>
        {!submitted ? (
          <div>
            {/* Honeypot */}
            <input
              type="text"
              name="website"
              value={honeypot}
              onChange={(e) => setHoneypot(e.target.value)}
              style={{ position: 'absolute', left: '-9999px' }}
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
            />

            {/* Email Label */}
            <label
              htmlFor="email"
              style={{ fontSize: '12px', color: '#9CA3AF', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.05em', display: 'block', marginBottom: '8px', fontFamily: "'IBM Plex Mono', monospace" }}
            >
              Email
            </label>

            {/* Email Input */}
            <input
              id="email"
              type="email"
              value={email}
              onChange={handleEmailChange}
              onKeyPress={(e) => e.key === 'Enter' && handleSubmit()}
              placeholder="email@example.com"
              className={`email-input ${emailError ? 'error' : ''}`}
              style={{ width: '100%', padding: '12px 16px', fontSize: '14px', marginBottom: '8px', fontFamily: "'IBM Plex Mono', monospace" }}
            />

            {/* Error/Correction Message */}
            {correctionApplied ? (
              <p style={{ color: '#22C55E', fontSize: '12px', marginBottom: '12px', fontFamily: "'IBM Plex Mono', monospace", display: 'flex', alignItems: 'center', gap: '4px' }}>
                <svg width="12" height="12" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
                Corrected your email
              </p>
            ) : emailError ? (
              <p style={{ color: '#EF4444', fontSize: '12px', marginBottom: '12px', fontFamily: "'IBM Plex Mono', monospace" }}>
                {emailError}
                {suggestedEmail && (
                  <span className="yes-link" onClick={applyCorrection}>yes</span>
                )}
              </p>
            ) : (
              <div style={{ height: '8px' }}></div>
            )}

            {/* Submit Button */}
            <button
              onClick={handleSubmit}
              disabled={submitting}
              className="submit-btn"
              style={{
                width: '100%',
                padding: '16px',
                color: 'white',
                fontSize: '14px',
                fontWeight: '500',
                fontFamily: "'IBM Plex Mono', monospace",
                cursor: submitting ? 'not-allowed' : 'pointer',
                border: 'none',
                opacity: submitting ? 0.7 : 1
              }}
            >
              {submitting ? 'Joining...' : 'Stay in the loop'}
            </button>
          </div>
        ) : (
          <div style={{ padding: '12px 16px', border: '1px solid #22C55E', background: '#F0FDF4', color: '#15803D', fontFamily: "'IBM Plex Mono', monospace", fontSize: '13px', borderRadius: '6px' }}>
            {successMessage}
          </div>
        )}
        </div>
      </main>

      {/* FOOTER_DISABLED — remove comment tags to restore
      <footer style={{ borderTop: '1px solid #E5E5E5', padding: '32px 24px', background: '#FCFCFC', display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
        <CompanyMark />
        <p style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: '11px', color: '#C0C0C0', margin: 0 }}>&copy; 2026 Watering Can</p>
      </footer>
      */}

      {/* ── Backdrop (visual only — pointer events handled by carousel wrapper) ── */}
      {carouselOpen && (
        <div
          style={{
            position: 'fixed', inset: 0, zIndex: 100,
            background: 'rgba(255,255,255,0.42)',
            backdropFilter: 'blur(16.6px)',
            WebkitBackdropFilter: 'blur(16.6px)',
            opacity: animPhase === 'entering' || animPhase === 'open' ? 1 : 0,
            transition: 'opacity 0.3s ease',
            pointerEvents: 'none',
          }}
        />
      )}

      {/* ── Carousel ── */}
      {carouselOpen && (
        <div
          onClick={closeCarousel}
          style={{
            position: 'fixed', inset: 0, zIndex: 200,
            display: 'flex', alignItems: 'center',
            opacity: animPhase === 'idle' || animPhase === 'closing' ? 0 : 1,
            transform: animPhase === 'idle' ? 'translateY(12px)' : 'translateY(0)',
            transition: 'opacity 0.3s ease, transform 0.3s ease',
            pointerEvents: animPhase === 'open' ? 'auto' : 'none',
          }}
        >
          <div ref={scrollRef} className="carousel-scroll" onClick={e => e.stopPropagation()}>
            {ALL_PHOTOS.map((src, i) => (
              <div key={i} className="carousel-scroll-item">
                <div className="polaroid-card polaroid-carousel-card">
                  <img src={src} alt={`Water for Plants gathering ${i + 1}`} />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      {carouselOpen && (
        <button className="carousel-close-btn" onClick={closeCarousel} aria-label="Close carousel">
          ×
        </button>
      )}
    </div>
  );
}
